import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Divider,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Chip,
  useTheme,
  useMediaQuery,
  IconButton,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  Person as PersonIcon,
  Notifications as NotificationsIcon,
  Security as SecurityIcon,
  CloudQueue as CloudIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Key as KeyIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  Sync as SyncIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Lock as LockIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  FormatListBulleted as ListIcon,
  CloudDone as CloudDoneIcon,
  CloudOff as CloudOffIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { checkServerHealth, testGoogleSheetsAuth } from '../services/serverService';

// Define types for our server status data
interface ServiceStatus {
  name: string;
  status: 'online' | 'offline' | 'warning' | 'loading';
  lastChecked: string;
  details?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Tab Panel Component
const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

// Tab props function
function a11yProps(index: number) {
  return {
    id: `settings-tab-${index}`,
    'aria-controls': `settings-tabpanel-${index}`,
  };
}

const Settings: React.FC = () => {
  // State for active tab
  const [value, setValue] = useState(0);
  
  // User data states
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    role: ''
  });
  
  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    newQrNotifications: true,
    returnNotifications: true,
  });
  
  // Security settings
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorAuth: false,
    loginNotifications: true,
    showSensitiveData: true,
  });
  
  // Server status states
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<string>('');
  
  // Theme and responsive design
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Auth and navigation
  const { currentUser, userData } = useAuth();
  const navigate = useNavigate();
  
  // Handle back button click
  const handleBack = () => {
    navigate('/dashboard');
  };
  
  // Initialize user data from auth context
  useEffect(() => {
    if (userData) {
      setProfileData({
        name: userData.name || '',
        email: currentUser?.email || '',
        phone: userData.phone || '',
        role: userData.role || 'User'
      });
    }
  }, [userData, currentUser]);
  
  // Check server status on component mount if tab is server status
  useEffect(() => {
    if (value === 3) {
      checkServerStatus();
    }
  }, [value]);
  
  // Handle tab change
  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  
  // Function to check all server services
  const checkServerStatus = async () => {
    setIsLoading(true);
    setError(null);
    const currentTime = new Date().toLocaleString();
    setLastRefresh(currentTime);

    try {
      // Create an array to store all service statuses
      const updatedServices: ServiceStatus[] = [];

      // Check the main API server health
      try {
        const healthResult = await checkServerHealth();
        updatedServices.push({
          name: 'API Server',
          status: healthResult.status === 'ok' ? 'online' : 'offline',
          lastChecked: currentTime,
          details: healthResult.message || ''
        });
      } catch (err) {
        updatedServices.push({
          name: 'API Server',
          status: 'offline',
          lastChecked: currentTime,
          details: 'Connection error'
        });
      }

      // Check Google Sheets integration
      try {
        const sheetsResult = await testGoogleSheetsAuth();
        updatedServices.push({
          name: 'Google Sheets Integration',
          status: sheetsResult.success ? 'online' : 'warning',
          lastChecked: currentTime,
          details: sheetsResult.message || ''
        });
      } catch (err) {
        updatedServices.push({
          name: 'Google Sheets Integration',
          status: 'offline',
          lastChecked: currentTime,
          details: 'Connection error'
        });
      }

      // Add Firebase server status (we'll just assume it's online if we can load this page)
      updatedServices.push({
        name: 'Firebase Hosting',
        status: 'online',
        lastChecked: currentTime,
        details: 'Connected'
      });

      // Add API Key status (we check this by the result of the API Server check)
      updatedServices.push({
        name: 'API Key Validation',
        status: updatedServices[0].status === 'online' ? 'online' : 'warning',
        lastChecked: currentTime,
        details: updatedServices[0].status === 'online' 
          ? 'API key is valid' 
          : 'Could not validate API key'
      });

      setServices(updatedServices);
    } catch (err) {
      setError('Failed to check server status. Please try again.');
      console.error('Server status check error:', err);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle profile save
  const handleSaveProfile = () => {
    // Here you would implement the actual save logic to your backend
    console.log('Saving profile:', profileData);
    setIsEditingProfile(false);
  };
  
  // Handle profile edit toggle
  const handleEditToggle = () => {
    setIsEditingProfile(!isEditingProfile);
  };
  
  // Handle notification setting change
  const handleNotificationChange = (setting: string) => {
    setNotificationSettings({
      ...notificationSettings,
      [setting]: !notificationSettings[setting as keyof typeof notificationSettings]
    });
  };
  
  // Handle security setting change
  const handleSecurityChange = (setting: string) => {
    setSecuritySettings({
      ...securitySettings,
      [setting]: !securitySettings[setting as keyof typeof securitySettings]
    });
  };
  
  // Get status chip color
  const getStatusChip = (status: 'online' | 'offline' | 'warning' | 'loading') => {
    let color: 'success' | 'error' | 'warning' | 'default' = 'default';
    let label = 'Unknown';

    switch (status) {
      case 'online':
        color = 'success';
        label = 'Online';
        break;
      case 'offline':
        color = 'error';
        label = 'Offline';
        break;
      case 'warning':
        color = 'warning';
        label = 'Warning';
        break;
      case 'loading':
        label = 'Checking...';
        break;
    }

    return <Chip label={label} color={color} size={isMobile ? 'small' : 'medium'} />;
  };
  
  // Calculate overall system status
  const getOverallStatus = (): 'online' | 'offline' | 'warning' => {
    if (services.some(service => service.status === 'offline')) {
      return 'offline';
    } else if (services.some(service => service.status === 'warning')) {
      return 'warning';
    } else {
      return 'online';
    }
  };
  
  return (
    <>
      {/* Top App Bar with Back Button */}
      <AppBar position="static" color="primary" elevation={0}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleBack}
            aria-label="back"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ ml: 1 }}>
            Settings
          </Typography>
        </Toolbar>
      </AppBar>
    
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Paper
          elevation={3}
          sx={{
            borderRadius: 2,
            overflow: 'hidden'
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs 
              value={value} 
              onChange={handleChange} 
              aria-label="settings tabs"
              variant={isMobile ? "scrollable" : "fullWidth"}
              scrollButtons={isMobile ? "auto" : undefined}
            >
              <Tab 
                icon={<PersonIcon />} 
                label={isMobile ? undefined : "Profile"} 
                iconPosition="start"
                {...a11yProps(0)} 
              />
              <Tab 
                icon={<NotificationsIcon />} 
                label={isMobile ? undefined : "Notifications"} 
                iconPosition="start"
                {...a11yProps(1)} 
              />
              <Tab 
                icon={<SecurityIcon />} 
                label={isMobile ? undefined : "Security"} 
                iconPosition="start"
                {...a11yProps(2)} 
              />
              <Tab 
                icon={<CloudIcon />} 
                label={isMobile ? undefined : "Server Status"} 
                iconPosition="start"
                {...a11yProps(3)} 
              />
            </Tabs>
          </Box>
          
          {/* Profile Tab */}
          <TabPanel value={value} index={0}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" component="h2">
                Profile Settings
              </Typography>
              <IconButton 
                color={isEditingProfile ? "primary" : "default"} 
                onClick={handleEditToggle}
                aria-label={isEditingProfile ? "Save profile" : "Edit profile"}
              >
                {isEditingProfile ? <SaveIcon /> : <EditIcon />}
              </IconButton>
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  variant="outlined"
                  value={profileData.name}
                  onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                  disabled={!isEditingProfile}
                  InputProps={{
                    startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  variant="outlined"
                  value={profileData.email}
                  onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                  disabled={!isEditingProfile}
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  variant="outlined"
                  value={profileData.phone}
                  onChange={(e) => setProfileData({...profileData, phone: e.target.value})}
                  disabled={!isEditingProfile}
                  InputProps={{
                    startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Role"
                  variant="outlined"
                  value={profileData.role}
                  disabled={true}
                />
              </Grid>
              
              {isEditingProfile && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button 
                      variant="contained" 
                      color="primary" 
                      onClick={handleSaveProfile}
                      startIcon={<SaveIcon />}
                    >
                      Save Changes
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </TabPanel>
          
          {/* Notifications Tab */}
          <TabPanel value={value} index={1}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" component="h2" gutterBottom>
                Notification Settings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage how you receive notifications and alerts
              </Typography>
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                      Notification Channels
                    </Typography>
                    
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <EmailIcon />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Email Notifications" 
                          secondary="Receive notifications via email" 
                        />
                        <Switch 
                          checked={notificationSettings.emailNotifications} 
                          onChange={() => handleNotificationChange('emailNotifications')} 
                          color="primary"
                        />
                      </ListItem>
                      
                      <Divider component="li" />
                      
                      <ListItem>
                        <ListItemIcon>
                          <NotificationsIcon />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Push Notifications" 
                          secondary="Receive push notifications in browser" 
                        />
                        <Switch 
                          checked={notificationSettings.pushNotifications} 
                          onChange={() => handleNotificationChange('pushNotifications')} 
                          color="primary"
                        />
                      </ListItem>
                      
                      <Divider component="li" />
                      
                      <ListItem>
                        <ListItemIcon>
                          <PhoneIcon />
                        </ListItemIcon>
                        <ListItemText 
                          primary="SMS Notifications" 
                          secondary="Receive notifications via SMS" 
                        />
                        <Switch 
                          checked={notificationSettings.smsNotifications} 
                          onChange={() => handleNotificationChange('smsNotifications')} 
                          color="primary"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                      Notification Types
                    </Typography>
                    
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <CloudIcon />
                        </ListItemIcon>
                        <ListItemText 
                          primary="New QR Creation" 
                          secondary="Get notified when new QR codes are created" 
                        />
                        <Switch 
                          checked={notificationSettings.newQrNotifications} 
                          onChange={() => handleNotificationChange('newQrNotifications')} 
                          color="primary"
                        />
                      </ListItem>
                      
                      <Divider component="li" />
                      
                      <ListItem>
                        <ListItemIcon>
                          <RefreshIcon />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Return Updates" 
                          secondary="Get notified about pending returns" 
                        />
                        <Switch 
                          checked={notificationSettings.returnNotifications} 
                          onChange={() => handleNotificationChange('returnNotifications')} 
                          color="primary"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<SaveIcon />}
                  >
                    Save Notification Settings
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </TabPanel>
          
          {/* Security Tab */}
          <TabPanel value={value} index={2}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="h5" component="h2" gutterBottom>
                Security Settings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage your account security and access settings
              </Typography>
            </Box>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                      Authentication
                    </Typography>
                    
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <LockIcon />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Two-Factor Authentication" 
                          secondary="Add an extra layer of security to your account" 
                        />
                        <Switch 
                          checked={securitySettings.twoFactorAuth} 
                          onChange={() => handleSecurityChange('twoFactorAuth')} 
                          color="primary"
                        />
                      </ListItem>
                      
                      <Divider component="li" />
                      
                      <ListItem>
                        <ListItemIcon>
                          <NotificationsIcon />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Login Notifications" 
                          secondary="Get notified of new logins to your account" 
                        />
                        <Switch 
                          checked={securitySettings.loginNotifications} 
                          onChange={() => handleSecurityChange('loginNotifications')} 
                          color="primary"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                      Data Privacy
                    </Typography>
                    
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <SecurityIcon />
                        </ListItemIcon>
                        <ListItemText 
                          primary="Show Sensitive Data" 
                          secondary="Show personal information in QR records" 
                        />
                        <Switch 
                          checked={securitySettings.showSensitiveData} 
                          onChange={() => handleSecurityChange('showSensitiveData')} 
                          color="primary"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button 
                    variant="outlined" 
                    color="error" 
                    sx={{ mr: 2 }}
                  >
                    Change Password
                  </Button>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<SaveIcon />}
                  >
                    Save Security Settings
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </TabPanel>
          
          {/* Server Status Tab */}
          <TabPanel value={value} index={3}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h5" component="h2">
                Server Status
              </Typography>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={checkServerStatus}
                disabled={isLoading}
              >
                Refresh
              </Button>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* System Status Summary */}
            <Card
              elevation={2}
              sx={{
                mb: 4,
                bgcolor: getOverallStatus() === 'online'
                  ? 'success.light'
                  : getOverallStatus() === 'warning'
                    ? 'warning.light'
                    : 'error.light',
                color: getOverallStatus() === 'online'
                  ? 'success.contrastText'
                  : getOverallStatus() === 'warning'
                    ? 'warning.contrastText'
                    : 'error.contrastText'
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {getOverallStatus() === 'online' ? (
                    <CloudDoneIcon fontSize="large" sx={{ mr: 2 }} />
                  ) : getOverallStatus() === 'warning' ? (
                    <WarningIcon fontSize="large" sx={{ mr: 2 }} />
                  ) : (
                    <CloudOffIcon fontSize="large" sx={{ mr: 2 }} />
                  )}
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      System Status: {getOverallStatus() === 'online' ? 'All Systems Operational' : getOverallStatus() === 'warning' ? 'Some Issues Detected' : 'System Outage'}
                    </Typography>
                    <Typography variant="body2">
                      Last checked: {lastRefresh}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Service Status List */}
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
              Service Status
            </Typography>

            <Grid container spacing={3}>
              {isLoading ? (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                </Grid>
              ) : (
                services.map((service, index) => (
                  <Grid item xs={12} sm={6} md={6} key={index}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
                            {service.name === 'API Server' && <SpeedIcon sx={{ mr: 1 }} />}
                            {service.name === 'Google Sheets Integration' && <StorageIcon sx={{ mr: 1 }} />}
                            {service.name === 'Firebase Hosting' && <SyncIcon sx={{ mr: 1 }} />}
                            {service.name === 'API Key Validation' && <KeyIcon sx={{ mr: 1 }} />}
                            {service.name}
                          </Typography>
                          {getStatusChip(service.status)}
                        </Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {service.details}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
              )}
            </Grid>

            {/* Server Information */}
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 4, mb: 2 }}>
              Server Information
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                      API Server Details
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><SettingsIcon /></ListItemIcon>
                        <ListItemText
                          primary="Server URL"
                          secondary="https://api-6ul3istoeq-uc.a.run.app"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><ListIcon /></ListItemIcon>
                        <ListItemText 
                          primary="Runtime" 
                          secondary="Node.js 18 (Firebase Functions)" 
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                      API Key Information
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon><KeyIcon /></ListItemIcon>
                        <ListItemText 
                          primary="Default API Key" 
                          secondary="sk_5eb6ee...6f36f97" 
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon><SettingsIcon /></ListItemIcon>
                        <ListItemText 
                          primary="Manage API Keys" 
                          secondary={
                            <Button 
                              size="small" 
                              href="/sheets-test" 
                              target="_blank" 
                              variant="outlined" 
                              sx={{ mt: 1 }}
                            >
                              Test API Integration
                            </Button>
                          } 
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Troubleshooting Guide */}
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mt: 4, mb: 2 }}>
              Troubleshooting
            </Typography>

            <Card variant="outlined" sx={{ mb: 4 }}>
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  If the server is offline:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon><RefreshIcon /></ListItemIcon>
                    <ListItemText 
                      primary="Restart the server" 
                      secondary="Use the Firebase Console or run 'firebase deploy --only functions:api'" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon><KeyIcon /></ListItemIcon>
                    <ListItemText 
                      primary="Check API Key" 
                      secondary="Ensure the API key is valid and properly configured" 
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </TabPanel>
        </Paper>
      </Container>
    </>
  );
};

export default Settings; 